import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Flag } from "lucide-react";
import SourceCards from "../dashboard5/SourceCards";
import StreamingMessage from "../dashboard5/StreamingMessage";
import CorrectionComposer from "./CorrectionComposer";
import { formatBotMessage, formatTimestamp, citedSourceNumbers } from "../dashboard5/utils/formatMessage";
import "./styles/transcript.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive which source numbers the answer actually cited, using the same
 * logic CopilotPanel uses (server-side `cited` flag first, prose scan
 * fallback). Duplicated as a standalone so this module stays import-free
 * of CopilotPanel itself.
 */
const citedNumbersFor = (msg) => {
  const sources = msg.sources || [];
  if (!sources.some((s) => typeof s.cited === "boolean")) {
    return citedSourceNumbers(msg.text, sources);
  }
  return new Set(
    sources
      .map((s, i) => (s.cited ? s.n || i + 1 : null))
      .filter((n) => n !== null)
  );
};

/**
 * Group a flat messages array into turns.
 *
 * A "turn" begins each time a user/agent message appears (sender is "user",
 * "agent", or the message is an answered_question). Everything that follows
 * until the next user message belongs to the same turn. Leading copilot
 * messages before any user message form turn 0 (e.g. a welcome or status).
 *
 * The turn NUMBER is the server's own (`msg.turnNo`, stamped by
 * TseDashboard5's appendMessage and by its replay walk) whenever the message
 * carries one. The local counter is only a fallback: it counts messages, so a
 * server turn with no user message shifts every later turn by one — and a
 * correction filed under a shifted number lands on the wrong answer. The
 * stamped number is only accepted when it moves FORWARD, so the numbers this
 * hands out stay unique and ordered even if a stamp is wrong.
 *
 * Returns [{turnNo: number, messages: [...]}].
 */
const groupIntoTurns = (messages) => {
  if (!messages || !messages.length) return [];
  const turns = [];
  let current = null;
  let turnCounter = 0;

  for (const msg of messages) {
    if (!msg) continue;
    // Skip session_clear_divider, feedback_prompt, context_switch_resume — these
    // are UI chrome that does not belong in a read-only review transcript.
    const t = msg.type;
    if (
      t === "session_clear_divider" ||
      t === "feedback_prompt" ||
      t === "context_switch_resume"
    ) continue;

    const isUserTurn =
      msg.sender === "user" ||
      msg.sender === "agent" ||
      t === "answered_question";

    if (isUserTurn) {
      const stamped = Number(msg.turnNo);
      turnCounter =
        Number.isFinite(stamped) && stamped > turnCounter ? stamped : turnCounter + 1;
      current = { turnNo: turnCounter, messages: [msg] };
      turns.push(current);
    } else if (current) {
      current.messages.push(msg);
    } else {
      // Pre-turn messages (status/product before any user msg).
      current = { turnNo: 0, messages: [msg] };
      turns.push(current);
    }
  }
  return turns;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** A single correction block, shown inside a turn. */
const CorrectionBlock = ({ c }) => {
  const status = (c.status || "reported").toLowerCase();
  const badgeCls =
    status === "published"
      ? "lt-correction-badge lt-correction-badge--published"
      : status === "discarded"
      ? "lt-correction-badge lt-correction-badge--discarded"
      : "lt-correction-badge";

  return (
    <div className="lt-correction">
      <div className="lt-correction-head">
        <span className={badgeCls}>{status}</span>
        {c.id && <span className="lt-correction-id">#{String(c.id).slice(0, 8)}</span>}
      </div>
      {c.wrong_text && (
        <div className="lt-correction-field">
          <span className="lt-correction-label">What the engine said</span>
          <span className="lt-correction-text">{c.wrong_text}</span>
        </div>
      )}
      {c.why_wrong && (
        <div className="lt-correction-field">
          <span className="lt-correction-label">Why it was wrong</span>
          <span className="lt-correction-text">{c.why_wrong}</span>
        </div>
      )}
      {c.correct_text && (
        <div className="lt-correction-field">
          <span className="lt-correction-label">What was correct</span>
          <span className="lt-correction-text">{c.correct_text}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Render one message inside a turn.
 *
 * Read-only, and deliberately class-for-class identical to CopilotPanel's
 * message branches (.d5-msg-block / .d5-msg / .d5-msg-bubble / .d5-msg-meta,
 * .d5-product-row, .d5-status-row, .d5-error-row, .d5-answered-block,
 * .d5-followups-block). The interactive bits are dropped — a click handler
 * here would have nothing to act on — but the MARKUP is the same, so the
 * dashboard5 rules give this pane the live panel's look for free instead of
 * a set of lt- lookalikes that drifted from it.
 */
const MessageRow = ({ msg, onFlagSource }) => {
  if (!msg) return null;
  const type = msg.type;

  if (type === "error") {
    return (
      <div className="d5-error-row">
        <span className="d5-error-icon">!</span>
        <span className="d5-error-text">{msg.text}</span>
      </div>
    );
  }

  if (type === "product") {
    return (
      <div className="d5-product-row">
        <span className="d5-product-label">Product identified</span>
        <span className="d5-product-badge">{msg.product}</span>
      </div>
    );
  }

  if (type === "status") {
    return (
      <div className="d5-status-row">
        <span className="d5-status-dot" />
        <span className="d5-status-text">{msg.text}</span>
      </div>
    );
  }

  if (type === "followups") {
    const opts = Array.isArray(msg.options) ? msg.options : [];
    if (!opts.length) return null;
    return (
      <div className="d5-msg-block d5-followups-block">
        <div className="d5-followups-card">
          <div className="d5-followups-title">Helpful suggestions</div>
          <div className="d5-followups-options">
            {opts.map((opt, i) => {
              const label = typeof opt === "string" ? opt : opt?.label ?? String(opt);
              const picked = msg.answered && msg.selected === opt;
              return (
                // A <button>, exactly as CopilotPanel renders it: the
                // .d5-followup-option rule sets no font-family, so a <span>
                // would inherit Space Grotesk while the live panel's button
                // falls back to the UA default. No handler — read-only.
                <button
                  key={i}
                  type="button"
                  tabIndex={-1}
                  className={`d5-followup-option${picked ? " d5-followup-option-picked" : ""}`}
                >
                  <span className="d5-followup-num">{i + 1}.</span>
                  <span className="d5-followup-text">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="d5-msg-meta">
          <span className="d5-msg-timestamp d5-msg-timestamp-visible">
            {formatTimestamp(msg.askedAt || msg.timestamp)}
          </span>
        </div>
      </div>
    );
  }

  if (type === "answered_question") {
    const { question, options = [] } = msg.questionSnapshot || {};
    return (
      <>
        <div className="d5-msg-block d5-answered-block">
          <div className="d5-answered-card">
            {question && (
              <div
                className="d5-answered-question"
                dangerouslySetInnerHTML={{ __html: formatBotMessage(question) }}
              />
            )}
            {options.length > 0 && (
              <div className="d5-answered-options">
                {options.map((opt, oi) => (
                  <div
                    key={oi}
                    className={`d5-answered-option ${opt === msg.selectedAnswer ? "d5-answered-option-picked" : ""}`}
                    dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="d5-msg-meta">
            <span className="d5-msg-timestamp d5-msg-timestamp-visible">
              {formatTimestamp(msg.askedAt || msg.timestamp)}
            </span>
          </div>
        </div>
        {msg.selectedAnswer && (
          <div className="d5-msg d5-msg-user d5-engineer-block">
            <div className="d5-msg-bubble d5-msg-bubble-user">{msg.selectedAnswer}</div>
            <div className="d5-msg-meta">
              <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                You {formatTimestamp(msg.timestamp)}
              </span>
            </div>
          </div>
        )}
      </>
    );
  }

  if (type === "case_analysis") {
    // CaseAnalysisPanels is heavy and interactive; the plain text the analysis
    // carries is enough for a review transcript. Rendered through
    // StreamingMessage so it still lands in a real .d5-msg-bubble.
    const text = msg.analysis?.case_analysis || "[Case Analysis]";
    return (
      <div className="d5-msg-block">
        <StreamingMessage
          text={text}
          isStreaming={false}
          isDone={true}
          timestamp={msg.timestamp}
          sender="copilot"
        />
      </div>
    );
  }

  // Live-agent message — its own branch for the same reason as in CopilotPanel:
  // the copilot branch would attribute a human agent's words to the AI, and the
  // user branch would draw them as the reader's own outgoing bubble.
  if (msg.sender === "live_agent") {
    return (
      <div className="d5-msg-block">
        <div className="d5-msg d5-msg-live-agent">
          <div
            className="d5-msg-bubble d5-msg-bubble-live-agent"
            dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }}
          />
          <div className="d5-msg-meta">
            <span className="d5-msg-timestamp d5-msg-timestamp-visible">
              {msg.agentName || "Support Agent"} {formatTimestamp(msg.timestamp)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const isCopilot = msg.sender === "copilot";

  if (isCopilot) {
    return (
      <div className="d5-msg-block">
        {msg.sources?.length > 0 && (
          <SourceCards
            sources={msg.sources}
            isPreview={msg.sourcesPreview}
            citedNumbers={citedNumbersFor(msg)}
            onFlagSource={onFlagSource}
          />
        )}
        <StreamingMessage
          text={msg.text}
          sources={msg.sources}
          isStreaming={false}
          isDone={true}
          cacheHit={!!msg.cacheHit}
          timestamp={msg.timestamp}
          sender="copilot"
        />
      </div>
    );
  }

  // User / agent message
  return (
    <div className="d5-msg-block">
      <div className="d5-msg d5-msg-user d5-engineer-block">
        {msg.attachments?.length > 0 && (
          <div className="d5-msg-attachments">
            {msg.attachments.map((a) =>
              a.kind === "image" && a.previewUrl ? (
                <img
                  key={a.id}
                  src={a.previewUrl}
                  alt={a.name}
                  className="d5-msg-attachment-thumb"
                />
              ) : (
                <span key={a.id || a.name} className="d5-msg-attachment-chip" title={a.name}>
                  <FileText size={12} />
                  {a.name}
                </span>
              )
            )}
          </div>
        )}
        {msg.text && <div className="d5-msg-bubble d5-msg-bubble-user">{msg.text}</div>}
        <div className="d5-msg-meta">
          <span className="d5-msg-timestamp d5-msg-timestamp-visible">
            {msg.sender === "agent" ? "You " : ""}
            {formatTimestamp(msg.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const LearningTranscript = ({
  messages = [],
  // Optional: turns ALREADY grouped as [{turnNo, messages}]. The in-session
  // overlay has a live `messages` array and lets groupIntoTurns number the
  // turns by counting user bubbles; /learning/{id} rebuilds the conversation
  // from stored envelopes that carry the server's own turn numbers, and those
  // must be used verbatim or a gap would renumber every turn after it and
  // break the step->turn scroll. Additive: the existing prop contract is
  // unchanged when this is omitted.
  groupedTurns,
  sessionId,
  // The engineer's opening line — used as fallback symptom in the composer.
  symptomText = "",
  corrections,
  activeTurn,
  onTurnClick,
  // Optional: called with the turn number currently in view when the USER
  // scrolls this pane. Additive — the existing prop contract is unchanged.
  onTurnInView,
  // Callback fired after a correction is successfully submitted, so the
  // parent can refresh trail data (and thus the corrections list).
  onCorrectionSent,
}) => {
  // Which correction composer is open, or null.
  // Shape: { type: "answer", turnNo } | { type: "source", turnNo, src, number }
  const [correcting, setCorrecting] = useState(null);

  const rootRef = useRef(null);
  const turnRefs = useRef({});
  const prevActiveTurnRef = useRef(null);
  // The last turn WE reported upward. Used to refuse re-scrolling for a turn
  // the pane is already sitting on (the other half of the loop guard).
  const emittedTurnRef = useRef(null);
  // Timestamp until which observer output is ignored — a programmatic
  // smooth-scroll sweeps every turn in between and would otherwise emit them.
  const suppressUntilRef = useRef(0);

  // Group messages into turns (unless the caller already did it).
  const turns = useMemo(
    () => (Array.isArray(groupedTurns) ? groupedTurns : groupIntoTurns(messages)),
    [groupedTurns, messages]
  );

  // Index corrections by turn_no for O(1) lookup.
  const { byTurn, orphans } = useMemo(() => {
    const map = {};
    const lost = [];
    if (!Array.isArray(corrections)) return { byTurn: map, orphans: lost };
    for (const c of corrections) {
      if (!c) continue;
      const tn = c.turn_no;
      if (tn != null && turns.some((t) => t.turnNo === tn)) {
        (map[tn] || (map[tn] = [])).push(c);
      } else {
        lost.push(c);
      }
    }
    return { byTurn: map, orphans: lost };
  }, [corrections, turns]);

  // Smooth-scroll to active turn and apply flash.
  useEffect(() => {
    if (activeTurn == null) return;
    if (activeTurn === prevActiveTurnRef.current) return;
    prevActiveTurnRef.current = activeTurn;

    // Loop guard: if this selection is the echo of our own scroll report,
    // the pane already shows it — scrolling again would fight the user.
    if (activeTurn === emittedTurnRef.current) return;

    const el = turnRefs.current[activeTurn];
    if (!el) return;

    // Remove previous flash classes.
    Object.values(turnRefs.current).forEach((node) => {
      if (node) node.classList.remove("lt-turn--flash");
    });

    // Mute the observer for the duration of the animated scroll, and record
    // that this turn is now the one on screen — otherwise the observer's
    // stale "last emitted" value would veto a later jump back to it.
    suppressUntilRef.current = Date.now() + 1200;
    emittedTurnRef.current = activeTurn;
    el.scrollIntoView({ behavior: "smooth", block: "start" });

    // Trigger flash animation.
    // Force reflow so adding the class always restarts the animation.
    void el.offsetWidth;
    el.classList.add("lt-turn--flash");
  }, [activeTurn]);

  // Keep the callback in a ref so re-creating it upstream does not tear down
  // and rebuild the observer on every render.
  const onTurnInViewRef = useRef(onTurnInView);
  useEffect(() => { onTurnInViewRef.current = onTurnInView; }, [onTurnInView]);

  // Watch which turn is in view and report it upward so the left pane can
  // follow along. Topmost visible turn wins; turn 0 (pre-conversation
  // chrome, no header) is not reported because nothing maps to it.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return undefined;

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const tn = Number(entry.target.getAttribute("data-turn-no"));
          if (!Number.isFinite(tn)) continue;
          if (entry.isIntersecting) visible.add(tn);
          else visible.delete(tn);
        }
        // Programmatic scroll in flight — do not bounce it back.
        if (Date.now() < suppressUntilRef.current) return;
        if (!visible.size) return;
        let best = null;
        for (const tn of visible) if (best === null || tn < best) best = tn;
        if (best === null || best === emittedTurnRef.current) return;
        emittedTurnRef.current = best;
        // Pre-set the scroll effect's guard: this turn is already on screen.
        prevActiveTurnRef.current = best;
        onTurnInViewRef.current?.(best);
      },
      {
        root,
        threshold: 0,
        // Only the top ~45% of the pane counts as "in view", so the turn the
        // reader is actually looking at wins over the one trailing below.
        rootMargin: "0px 0px -55% 0px",
      }
    );

    const nodes = Object.entries(turnRefs.current)
      .filter(([tn, node]) => node && Number(tn) > 0)
      .map(([, node]) => node);
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [turns]);

  /** Extract the copilot answer text + source nids from a turn's messages. */
  const copilotPayloadFor = useCallback((turn) => {
    const copilotMsg = turn.messages.find(
      (m) => m.sender === "copilot" && (m.text || "").trim()
    );
    if (!copilotMsg) return null;
    return {
      wrongText: (copilotMsg.text || "").trim().slice(0, 2000),
      // Same fallback as the per-source composer below: faststage source cards
      // carry `id`/`card_key` and never `nid`, so reading `nid` alone posted an
      // empty list on every fast-mode answer.
      wrongNids: (copilotMsg.sources || []).map((x) => x?.nid || x?.id).filter(Boolean),
    };
  }, []);

  /** Called when the inline composer finishes (sent or cancelled). */
  const handleCorrectionDone = useCallback(
    (wasSent) => {
      setCorrecting(null);
      if (wasSent) onCorrectionSent?.();
    },
    [onCorrectionSent]
  );

  if (turns.length === 0) {
    return (
      <div className="lt-root">
        <div className="lt-empty">
          <span className="lt-empty-title">No messages</span>
          <span>This session has no conversation to display.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="lt-root" ref={rootRef}>
      {turns.map((turn) => {
        const isActive = activeTurn != null && turn.turnNo === activeTurn;
        const turnCorrections = byTurn[turn.turnNo] || [];
        return (
          <div
            key={turn.turnNo}
            ref={(el) => { turnRefs.current[turn.turnNo] = el; }}
            data-turn-no={turn.turnNo}
            className={`lt-turn${isActive ? " lt-turn--active" : ""}`}
          >
            {turn.turnNo > 0 && (
              <div
                className="lt-turn-header"
                onClick={() => onTurnClick?.(turn.turnNo)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTurnClick?.(turn.turnNo);
                  }
                }}
              >
                <span className="lt-turn-label">Turn {turn.turnNo}</span>
                <span className="lt-turn-divider" />
              </div>
            )}
            {turn.messages.map((msg, mi) => (
              <MessageRow
                key={msg.id || `${turn.turnNo}-${mi}`}
                msg={msg}
                onFlagSource={
                  turn.turnNo > 0
                    ? (src, number) =>
                        setCorrecting({ type: "source", turnNo: turn.turnNo, src, number })
                    : undefined
                }
              />
            ))}

            {/* Existing corrections for this turn */}
            {turnCorrections.map((c) => (
              <CorrectionBlock key={c.id || `c-${turn.turnNo}-${Math.random()}`} c={c} />
            ))}

            {/* Per-source inline correction composer.
                Shown when a specific source card's flag was clicked on this turn.
                Rendered BEFORE the per-answer flag so it appears near the cards. */}
            {correcting?.type === "source" && correcting.turnNo === turn.turnNo && (() => {
              const { src, number } = correcting;
              return (
                <div className="lt-inline-composer">
                  <CorrectionComposer
                    sessionId={sessionId}
                    turnNo={turn.turnNo}
                    symptomText={symptomText}
                    wrongText={`Source ${number}: ${src?.title || src?.nid || "(untitled)"}`}
                    wrongNids={[src?.nid || src?.id].filter(Boolean)}
                    onDone={handleCorrectionDone}
                  />
                </div>
              );
            })()}

            {/* Per-answer flag affordance + inline correction composer.
                Only shown on turns that have a copilot answer (turn > 0) and
                are not already corrected. A turn that already has a correction
                shows it above — filing a silent second one is prevented. */}
            {turn.turnNo > 0 && (() => {
              const hasCopilot = copilotPayloadFor(turn) != null;
              const alreadyCorrected = turnCorrections.length > 0;
              if (!hasCopilot) return null;

              if (correcting?.type === "answer" && correcting.turnNo === turn.turnNo) {
                const payload = copilotPayloadFor(turn);
                return (
                  <div className="lt-inline-composer">
                    <CorrectionComposer
                      sessionId={sessionId}
                      turnNo={turn.turnNo}
                      symptomText={symptomText}
                      wrongText={payload.wrongText}
                      wrongNids={payload.wrongNids}
                      onDone={handleCorrectionDone}
                    />
                  </div>
                );
              }

              // Hide the per-answer flag when a per-source composer is open on
              // this turn, or when the turn is already corrected.
              if (alreadyCorrected) return null;
              if (correcting?.type === "source" && correcting.turnNo === turn.turnNo) return null;

              return (
                <button
                  type="button"
                  className="lt-flag-btn"
                  title="Flag this answer as wrong"
                  onClick={() => setCorrecting({ type: "answer", turnNo: turn.turnNo })}
                >
                  <Flag size={11} strokeWidth={2.5} />
                  Flag As Wrong
                </button>
              );
            })()}
          </div>
        );
      })}

      {orphans.length > 0 && (
        <div className="lt-orphan-section">
          <div className="lt-orphan-title">Corrections (unmatched turn)</div>
          {orphans.map((c, i) => (
            <CorrectionBlock key={c.id || `orphan-${i}`} c={c} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LearningTranscript;
